import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';

//common
import TitleContainer from '../../../components/Title/TitleContainer';
import TitleWithIcon from '../../../components/Title/TitleWithIcon';
import { PercentFormat } from '../../../commons/utils';
import useAssetTheme from '../../../stores/useAssetTheme';
//component
import DateSelector from '../../../components/Selector/dateSelector';
import JTable from '../../../components/Table/JTable';
import SwitchSelector from '../../../components/Selector/switchSelector';
import JTooltip from '../../../components/Tooltip/JTooltip';

//icon
import { ReactComponent as ListWithDotIcon } from '../../../images/svg/list_with_dot.svg';

import { useProvided } from '../../../stores/index';
import useAuth from '../../../stores/useAuth';

const GenerateColumns = simulate => {
  const rtnVal = [];
  const subTitles = ['近一週', '近一月', '近一季', '近一年'];
  const subFields = ['week', 'month', 'quarter', 'year'];

  rtnVal.push({
    title: '電廠',
    field: 'pvId',
    render: row => (
      <div>
        <div>{row.pvId && row.pvId + '-'}</div>
        <div>{row.pvName}</div>
      </div>
    ),
    cellStyle: {
      width: '200px'
    }
  });
  rtnVal.push({
    title: '資產占比',
    field: 'weight',
    render: row => <span>{PercentFormat(row.weight)}</span>,
    headerStyle: {
      paddingRight: '30px'
    },
    cellStyle: {
      width: '80px',
      paddingRight: '30px'
    }
  });

  const innerDom = [];
  for (let i = 0; i < subTitles.length; i++) {
    const fieldName = simulate ? subFields[i] + 'Sim' : subFields[i];
    innerDom.push({
      title: subTitles[i],
      field: fieldName,
      render: row => <span>{PercentFormat(row[fieldName])}</span>,
      cellStyle: {
        width: '100px'
      }
    });
  }

  rtnVal.push({
    title: '年平均發電達成率',
    subColumns: innerDom
  });

  return rtnVal;
};

const tableDesc = [
  { colName: '資產占比', colDesc: '投入該電廠的金額 ÷ 投入所有電廠的金額 × 100%。' },
  {
    colName: '近一週',
    colDesc: '所選日期回推一週的平均每日發電量 × 365 ÷ (電廠年度保證發電量 × 容量) × 100%。'
  },
  {
    colName: '近一月',
    colDesc: '所選日期回推一月的平均每日發電量 × 365 ÷ (電廠年度保證發電量 × 容量) × 100%。'
  },
  {
    colName: '近一季',
    colDesc: '所選日期回推一季的平均每日發電量 × 365 ÷ (電廠年度保證發電量 × 容量) × 100%。'
  },
  {
    colName: '近一年',
    colDesc: '所選日期回推一年的平均每日發電量 × 365 ÷ (電廠年度保證發電量 × 容量) × 100%。'
  }
];
const CancelFnList = [];
const AchieveRateRecent = () => {
  const { solaTheme } = useProvided(useAssetTheme);
  const [selectDate, setSelectDate] = useState(moment().format('YYYY-MM-DD'));
  const [recentData, setRecentData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);

  const [simulate, setSimulate] = useState(true);
  const { currentSPV } = useProvided(useAuth);

  const handleDatePickerChange = newDate => {
    if (selectDate !== newDate) {
      setSelectDate(newDate);
    }
  };
  const handleSimulateSwitch = () => {
    setSimulate(s => !s);
  };

  const tableColumns = useMemo(() => {
    return GenerateColumns(simulate);
  }, [simulate]);

  //Data Loading.
  const loadAchieveRate = useCallback(() => {
    const source = axios.CancelToken.source();
    CancelFnList.push(source.cancel);
    axios
      .get(`/api/v1/assets/achieverate/recent?date=${selectDate}`, {
        headers: { Authorization: currentSPV.token },
        cancelToken: source.token
      })
      .then(res => {
        if (res && res.data && res.data.status && res.data.result) {
          setRecentData(res.data.result.achieveRateRecent.achieveRate);
          setSummaryData(res.data.result.achieveRateRecent.summary);
        } else {
          setRecentData([]);
          setSummaryData([]);
        }
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          console.error(err);
        }
      });
  }, [currentSPV, selectDate]);

  const colors = {
    conditionContainerBgColor: solaTheme.componentColors.ConditionContainerBgColor,
    hrColor: solaTheme.componentColors.TitleContainerHrColor
  };

  //僅載入乙次
  useEffect(() => {
    loadAchieveRate();

    return () => {
      if (CancelFnList && CancelFnList.length > 0) {
        CancelFnList.forEach(fn => {
          fn();
        });
      }
    };
  }, [loadAchieveRate]);

  return (
    <div>
      <TitleContainer>
        <TitleWithIcon title="達成率分析(近期)" prefixIcon={ListWithDotIcon} />
      </TitleContainer>
      <ConditionContainer {...colors}>
        <JTooltip desc={tableDesc} />
        <DateSelectorBlock>
          <DateSelector selectDate={selectDate} dateChangeFn={handleDatePickerChange} navButton={false} />
        </DateSelectorBlock>

        <SwitchSelector label="含推估收益" value={simulate} changeFn={handleSimulateSwitch} />
      </ConditionContainer>
      <JTable columns={tableColumns} data={recentData} summaryData={summaryData} />
    </div>
  );
};

const ConditionContainer = styled.div`
  height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 15px;
  padding-right: 7px;
  background-color: ${p => p.conditionContainerBgColor};
  border-bottom: ${p => (p.transparent ? '0px' : `1px solid ${p.hrColor}`)};
`;
const DateSelectorBlock = styled.div`
  width: 80%;
  text-align: cetner;
`;

export default AchieveRateRecent;
